import { getSaleStatusPresentation } from '@entities/sale'
import { Money } from '@shared/lib/money/money'
import type { StatusTone } from '@shared/design-system/ui'
import type { Currency } from '@shared/contracts/enums'
import type { MoneyDto, SaleItem } from '../api/admin.api'

export function adminSaleStatusPresentation(sale: SaleItem): {
  label: string
  tone: StatusTone
} {
  const latest = sale.financialEvents?.filter((event) => event.kind !== 'FX_LOSS').at(-1)
  if (latest?.kind === 'PARTIAL_REFUND') {
    return { label: 'Reembolso parcial', tone: 'informative' }
  }
  if (latest?.kind === 'DISPUTE_WON' && sale.status !== 'CHARGED_BACK') {
    return { label: 'Disputa ganha', tone: 'success' }
  }
  if (sale.status === 'REFUNDED' && sale.handledByGlobalChargeback) {
    return { label: 'Reembolso de chargeback', tone: 'closed' }
  }
  const presentation = getSaleStatusPresentation(
    sale.status as Parameters<typeof getSaleStatusPresentation>[0],
  )
  return { label: presentation.label ?? sale.status, tone: presentation.tone }
}

export function saleFinancialImpact(sale: SaleItem): {
  original: MoneyDto
  active: MoneyDto
  affected: MoneyDto
  hasImpact: boolean
} {
  const original = Money.fromMonetaryValue(asMonetaryValue(sale.grossAmount))
  let affected = new Money(0, original.currency)
  const events = sale.financialEvents ?? []
  if (events.length === 0 && (sale.status === 'REFUNDED' || sale.status === 'CHARGED_BACK')) {
    affected = original
  } else {
    for (const event of events) {
      if (event.kind === 'FX_LOSS' || event.kind === 'DISPUTE_WON' || event.state === 'REVERSED')
        continue
      const amount = event.chargeAffectedAmount ?? event.affectedAmount
      if (amount.currency === original.currency)
        affected = affected.add(Money.fromMonetaryValue(asMonetaryValue(amount)))
    }
    if (affected.toNumber() > original.toNumber()) affected = original
  }
  return {
    original: original.toMonetaryValue(),
    active: original.subtract(affected).toMonetaryValue(),
    affected: affected.toMonetaryValue(),
    hasImpact: !affected.isZero(),
  }
}

function asMonetaryValue(value: MoneyDto): { amount: string; currency: Currency } {
  return value as { amount: string; currency: Currency }
}

export function adminSaleLineKindLabel(kind: string): string {
  return (
    {
      PRINCIPAL: 'Produto principal',
      BUMP: 'Order bump',
      ORDER_BUMP: 'Order bump',
      UPSELL: 'Upsell',
      DOWNSELL: 'Downsell',
    }[kind] ?? kind
  )
}
